import { useState, type FormEvent } from 'react'
import { AlertCircle, Eye, EyeOff, KeyRound } from 'lucide-react'
import { AuthShell } from './AuthShell.js'
import { consumeReturnTo } from '../shell/auth-routing.js'

/**
 * /login/new-password — remplacement OBLIGATOIRE du mot de passe temporaire.
 *
 * La session courante est en état PASSWORD_CHANGE : elle ne donne accès à
 * rien d'autre. Le nouveau mot de passe part du navigateur vers
 * POST /api/v1/auth/password/forced, et nulle part ailleurs. Après succès :
 * rotation de session, puis MFA à activer si nécessaire.
 */
export function NewPasswordPage() {
  const [pwd, setPwd] = useState('')
  const [pwd2, setPwd2] = useState('')
  const [reveal, setReveal] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (busy) return
    if (pwd !== pwd2) { setError('Les deux saisies diffèrent.'); return }
    setBusy(true)
    setError(null)
    try {
      const s = await fetch('/api/v1/auth/session', { credentials: 'same-origin' }).then((r) => r.json()) as { csrf_token: string | null; state: string | null }
      if (s.state !== 'PASSWORD_CHANGE') { window.location.assign('/login'); return }
      const r = await fetch('/api/v1/auth/password/forced', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'content-type': 'application/json', 'x-csrf-token': s.csrf_token ?? '' },
        body: JSON.stringify({ next: pwd }),
      })
      const j = await r.json() as { ok: boolean; state?: string; message?: string }
      if (j.ok) {
        // Le retour prévu (ex. « Autoriser LC Porte ») survit au premier mot de passe ; l'enrôlement MFA le consommera.
        window.location.assign(j.state === 'MFA_ENROLLMENT_REQUIRED' ? '/login/mfa-setup' : consumeReturnTo('/'))
        return
      }
      setError(j.message ?? 'Le mot de passe n’a pas été enregistré. Réessayez.')
    } catch {
      setError('Le serveur est injoignable. Réessayez dans un instant.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthShell
      title="Choisissez votre mot de passe"
      subtitle="Le mot de passe temporaire ne sera plus jamais accepté"
    >
      <form className="lc-form" onSubmit={submit} noValidate>
        {error ? (
          <p className="lc-form__error" role="alert">
            <AlertCircle size={16} aria-hidden="true" />
            {error}
          </p>
        ) : null}

        <div className="lc-field">
          <label className="lc-label" htmlFor="np-pwd">Nouveau mot de passe</label>
          <div className="lc-field__wrap">
            <input
              id="np-pwd"
              className="lc-input lc-input--haspad"
              type={reveal ? 'text' : 'password'}
              name="new-password"
              autoComplete="new-password"
              required
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              disabled={busy}
            />
            <button type="button" className="lc-field__reveal" onClick={() => setReveal(!reveal)}
              aria-label={reveal ? 'Masquer' : 'Afficher'} aria-pressed={reveal}>
              {reveal ? <EyeOff size={17} /> : <Eye size={17} />}
            </button>
          </div>
          <p className="lc-form__help">
            Au moins 12 caractères — une phrase de passe fonctionne très bien.
          </p>
        </div>

        <div className="lc-field">
          <label className="lc-label" htmlFor="np-pwd2">Confirmer</label>
          <input
            id="np-pwd2"
            className="lc-input"
            type={reveal ? 'text' : 'password'}
            autoComplete="new-password"
            required
            value={pwd2}
            onChange={(e) => setPwd2(e.target.value)}
            disabled={busy}
          />
        </div>

        <button className="lc-auth__cta" type="submit" disabled={busy || pwd.length < 12 || pwd2.length < 12}>
          {busy ? <span className="lc-spin" aria-hidden="true" /> : <KeyRound size={17} aria-hidden="true" />}
          {busy ? 'Enregistrement…' : 'Définir ce mot de passe'}
        </button>
      </form>
    </AuthShell>
  )
}
